import React, { memo, useMemo, useEffect, useState } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CardContent,
  Card,
  Typography,
  Box,
  Grid,
  Chip,
  Tooltip,
  Divider,
  useTheme,
  useMediaQuery,
  Skeleton,
  Alert,
  IconButton,
  Button,
  Collapse,
  Badge,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PeopleIcon from "@mui/icons-material/People";
import NutriPotSelector from "./NutriPotSelector";
import StatusIndicator from "./StatusIndicator";
import CategoryRibbon from "./CategoryRibbon";
import { servingPersons } from "../../Static/inventory";
import FoodTypeIndicator from "./FoodTypeIndicator";
import { formatTime, getRecipeImage } from "../../constants";
import PersonIcon from "@mui/icons-material/Person";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const RecipeImage = memo(function RecipeImage({ recipe, isMobile, loading }) {
  if (loading) {
    return <Skeleton variant="rectangular" width="100%" height="100%" />;
  }

  return (
    <>
      <img
        src={getRecipeImage(recipe?.recipe_image) || "/placeholder-16x9.png"}
        alt={recipe?.recipe_name}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "center",
          filter: "brightness(0.9)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: 16,
          left: 16,
          right: 16,
          zIndex: 1,
        }}
      >
        <Typography
          variant={isMobile ? "h5" : "h4"}
          fontWeight="bold"
          color="white"
        >
          {recipe?.recipe_name}
        </Typography>
      </Box>
    </>
  );
});

const TimeChips = memo(function TimeChips({ recipe, isMobile, loading }) {
  if (loading) {
    return (
      <>
        <Skeleton variant="rounded" width={100} height={32} />
        <Skeleton variant="rounded" width={90} height={32} />
        <Skeleton variant="rounded" width={80} height={32} />
      </>
    );
  }

  return (
    <>
      <Chip
        icon={<AccessTimeIcon fontSize={isMobile ? "small" : "medium"} />}
        label={`Total: ${formatTime(recipe?.recipe_cooking_duration)}`}
        size="small"
        color="secondary"
        variant="outlined"
      />
      <Chip
        label={`Prep: ${formatTime(recipe?.prep_time)}`}
        size="small"
        color="info"
        variant="outlined"
      />
      <Chip
        label={`Cook: ${formatTime(recipe?.cook_time)}`}
        size="small"
        color="warning"
        variant="outlined"
      />
    </>
  );
});

const ServingSelector = memo(function ServingSelector({
  servingCount,
  onServingChange,
  isMobile,
  loading,
}) {
  const [showAllOptions, setShowAllOptions] = useState(false);

  // Quick selection options (most common serving sizes)
  const quickOptions = [1, 2, 4, 6];

  // All available options from servingPersons
  const allOptions = servingPersons.map((opt) => opt.value);

  if (loading) {
    return <Skeleton variant="rounded" height={isMobile ? 120 : 140} />;
  }

  const handleQuickSelect = (value) => {
    onServingChange(value);
  };

  const handleStepperChange = (increment) => {
    const currentIndex = allOptions.indexOf(servingCount);
    let newIndex;

    if (increment) {
      newIndex = Math.min(currentIndex + 1, allOptions.length - 1);
    } else {
      newIndex = Math.max(currentIndex - 1, 0);
    }

    onServingChange(allOptions[newIndex]);
  };

  const renderPersonIcon = (count) => {
    const icons = [];
    const maxIcons = Math.min(count, 6); // Show max 6 icons

    for (let i = 0; i < maxIcons; i++) {
      icons.push(
        <PersonIcon
          key={i}
          sx={{
            fontSize: isMobile ? "0.875rem" : "1rem",
            opacity: 0.8,
            mr: 0.25,
          }}
        />
      );
    }

    if (count > 6) {
      icons.push(
        <Typography
          key="more"
          variant="caption"
          sx={{
            ml: 0.5,
            fontSize: isMobile ? "0.75rem" : "0.8rem",
            opacity: 0.7,
          }}
        >
          +{count - 6}
        </Typography>
      );
    }

    return icons;
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 2,
          gap: 1,
        }}
      >
        <PeopleIcon
          sx={{
            color: "primary.main",
            fontSize: isMobile ? "1.25rem" : "1.5rem",
          }}
        />
        <Typography
          variant={isMobile ? "subtitle2" : "subtitle1"}
          fontWeight="600"
          color="text.primary"
        >
          Select Servings
        </Typography>
      </Box>

      {/* Quick Selection Cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: isMobile ? 1 : 1.5,
          mb: 2,
        }}
      >
        {quickOptions.map((option) => (
          <Card
            key={option}
            onClick={() => handleQuickSelect(option)}
            sx={{
              cursor: "pointer",
              border: 2,
              borderColor: servingCount === option ? "primary.main" : "divider",
              bgcolor:
                servingCount === option ? "primary.50" : "background.paper",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              transform: servingCount === option ? "scale(1.05)" : "scale(1)",
              boxShadow: servingCount === option ? 3 : 1,
              "&:hover": {
                transform: "scale(1.02)",
                boxShadow: 2,
                borderColor: "primary.main",
              },
              borderRadius: 2,
              minHeight: isMobile ? 60 : 70,
            }}
          >
            <CardContent
              sx={{
                p: isMobile ? 1 : 1.5,
                textAlign: "center",
                "&:last-child": { pb: isMobile ? 1 : 1.5 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    mb: 0.5,
                  }}
                >
                  {renderPersonIcon(option)}
                </Box>
                <Typography
                  variant={isMobile ? "caption" : "body2"}
                  fontWeight="600"
                  color={
                    servingCount === option ? "primary.main" : "text.secondary"
                  }
                >
                  {option}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Stepper Controls */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          p: 1,
          bgcolor: "grey.50",
          borderRadius: 2,
          border: 1,
          borderColor: "divider",
        }}
      >
        <IconButton
          onClick={() => handleStepperChange(false)}
          disabled={servingCount === allOptions[0]}
          size={isMobile ? "small" : "medium"}
          sx={{
            bgcolor: "background.paper",
            boxShadow: 1,
            "&:hover": { boxShadow: 2 },
            "&:disabled": {
              bgcolor: "grey.100",
              boxShadow: 0,
            },
          }}
        >
          <RemoveIcon />
        </IconButton>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            minWidth: 80,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 0.5,
            }}
          >
            {renderPersonIcon(servingCount)}
          </Box>
          <Typography
            variant={isMobile ? "h6" : "h5"}
            fontWeight="700"
            color="primary.main"
          >
            {servingCount}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: isMobile ? "0.7rem" : "0.75rem" }}
          >
            {servingCount === 1 ? "person" : "people"}
          </Typography>
        </Box>

        <IconButton
          onClick={() => handleStepperChange(true)}
          disabled={servingCount === allOptions[allOptions.length - 1]}
          size={isMobile ? "small" : "medium"}
          sx={{
            bgcolor: "background.paper",
            boxShadow: 1,
            "&:hover": { boxShadow: 2 },
            "&:disabled": {
              bgcolor: "grey.100",
              boxShadow: 0,
            },
          }}
        >
          <AddIcon />
        </IconButton>
      </Box>

      {/* Expandable All Options */}
      <Box sx={{ mt: 2 }}>
        <Button
          variant="text"
          size="small"
          onClick={() => setShowAllOptions(!showAllOptions)}
          endIcon={showAllOptions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{
            fontSize: isMobile ? "0.75rem" : "0.875rem",
            textTransform: "none",
          }}
        >
          {showAllOptions ? "Show Less" : "More Options"}
        </Button>

        <Collapse in={showAllOptions}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(60px, 1fr))",
              gap: 1,
              mt: 1,
            }}
          >
            {allOptions
              .filter((option) => !quickOptions.includes(option))
              .map((option) => (
                <Chip
                  key={option}
                  label={option}
                  onClick={() => handleQuickSelect(option)}
                  variant={servingCount === option ? "filled" : "outlined"}
                  color={servingCount === option ? "primary" : "default"}
                  sx={{
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      transform: "scale(1.05)",
                    },
                    fontWeight: servingCount === option ? 600 : 400,
                  }}
                />
              ))}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
});

function RecipeDetailsPanel({
  recipe,
  servingCount,
  onServingChange,
  stationSelected,
  onStationChange,
  isReady,
  prevTask,
  onCustomizeRecipe,
  customizableTasksCount,
  children,
  sx,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const loading = !recipe;
  const prevStationUsed = prevTask?.tasks?.[0]?.cookIn;

  useEffect(() => {
    if (recipe && stationSelected) {
      onStationChange(stationSelected);
    }
  }, [recipe?.id]);

  const styles = useMemo(
    () => ({
      card: {
        position: "relative",
        borderRadius: 2,
        overflow: "hidden",
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: 6,
        },
        ...sx,
      },
      imageContainer: {
        height: isMobile ? 180 : 240,
        position: "relative",
        overflow: "hidden",
        "&:after": {
          content: '""',
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "40%",
          background:
            "linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)",
        },
      },
      categoryRibbon: {
        right: isMobile ? -4 : -8,
        "& span": {
          fontSize: isMobile ? "0.65rem" : "0.75rem",
          px: isMobile ? 1.5 : 2,
        },
      },
      statusIndicator: {
        p: isMobile ? 1.5 : 2,
        borderRadius: 2,
        backgroundColor: isReady ? "success.lighter" : "warning.lighter",
        border: `2px solid ${
          isReady ? theme.palette.success.main : theme.palette.warning.main
        }`,
        "& .MuiChip-root": {
          fontSize: isMobile ? "0.875rem" : "1rem",
        },
      },
    }),
    [
      isMobile,
      sx,
      isReady,
      theme.palette.success.main,
      theme.palette.warning.main,
    ]
  );

  return (
    <Card elevation={4} sx={styles.card}>
      <CategoryRibbon
        categories={recipe?.recipe_category}
        sx={styles.categoryRibbon}
      />

      <Box sx={styles.imageContainer}>
        <RecipeImage recipe={recipe} isMobile={isMobile} loading={loading} />
      </Box>

      <CardContent sx={{ pt: isMobile ? 2 : 3, pb: isMobile ? 1 : 2 }}>
        <Box
          display="flex"
          gap={1}
          mb={2}
          flexWrap="wrap"
          sx={{
            "& .MuiChip-root": {
              borderRadius: 1,
              fontSize: isMobile ? "0.75rem" : "0.875rem",
            },
          }}
        >
          <TimeChips recipe={recipe} isMobile={isMobile} loading={loading} />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={isMobile ? 1 : 3}>
          <Grid item xs={12} md={6} sx={{ mb: isMobile ? 1 : 0 }}>
            <ServingSelector
              servingCount={servingCount}
              onServingChange={onServingChange}
              isMobile={isMobile}
              loading={loading}
            />
          </Grid>

          {/* Customize Recipe Button */}
          <Grid item xs={12} md={6} sx={{ mb: isMobile ? 2 : 0 }}>
            {loading ? (
              <Skeleton variant="rounded" height={56} />
            ) : (
              onCustomizeRecipe && (
                <Box
                  sx={{ height: "100%", display: "flex", alignItems: "center" }}
                >
                  <Button
                    variant="contained"
                    onClick={onCustomizeRecipe}
                    fullWidth
                    startIcon={
                      <Badge
                        badgeContent={customizableTasksCount}
                        color="secondary"
                        max={99}
                        anchorOrigin={{
                          vertical: "top",
                          horizontal: "right",
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            fontSize: "1.2rem",
                          }}
                        >
                          🔧
                        </Box>
                      </Badge>
                    }
                    sx={{
                      minHeight: 56,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      "&:hover": {
                        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                        transform: "scale(1.02)",
                      },
                      transition: "all 0.3s ease",
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: isMobile ? "0.875rem" : "1rem",
                    }}
                  >
                    Customize Recipe
                  </Button>
                </Box>
              )
            )}
          </Grid>

          <Grid item xs={12} md={12}>
            {loading ? (
              <Skeleton variant="rounded" height={56} />
            ) : (
              <>
                {prevStationUsed && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Previously selected recipe is using :{" "}
                    <strong>
                      {prevStationUsed === "np1" ? "Pot 1" : "Pot 2"}
                    </strong>
                    . Please select a different Pot.
                  </Alert>
                )}
                <NutriPotSelector
                  value={stationSelected}
                  onChange={onStationChange}
                  disabledValue={prevStationUsed}
                  sx={{
                    "& .MuiTypography-root": {
                      fontSize: isMobile ? "0.875rem" : "1rem",
                    },
                  }}
                />
              </>
            )}
          </Grid>
        </Grid>

        <Box mt={3}>
          {loading ? (
            <Skeleton variant="rounded" height={64} />
          ) : (
            <StatusIndicator
              label="Cooking Readiness"
              status={isReady}
              trueText="All Set - Ready to Cook!"
              falseText="Setup Required"
              sx={styles.statusIndicator}
            />
          )}
        </Box>

        {children && <Box mt={3}>{children}</Box>}
      </CardContent>
    </Card>
  );
}

RecipeDetailsPanel.displayName = "RecipeDetailsPanel";

export default memo(RecipeDetailsPanel);
